import type { PlanningBaseline } from '@@/api/statistics/planning/planning-baseline';
import type { PlanAssumptions } from 'helpers/plan-assumptions';
import { calculateRequiredSampleSize } from '@@/api/statistics/methods/power-analysis';
import { DEFAULT_MIN_RUNTIME_MS } from '@@/api/experiments/lifecycle/stopping-rule';

/**
 * How big and how long an experiment has to be, before it has any data.
 *
 * The same sizing equation as the running experiment's power panel (power-analysis.ts),
 * so a plan made on a draft and the projection shown once it runs cannot disagree about
 * the maths, only about the inputs. Two adjustments are specific to planning:
 *
 *  - Alpha is split across the comparisons (Bonferroni). The results screen corrects
 *    with Holm by default, whose strictest step is exactly Bonferroni, so sizing for it
 *    guarantees the plan is enough whichever variant ends up being tested first.
 *  - The run is limited by the arm with the smallest share of traffic. Every
 *    comparison needs the required sample in both arms, and an arm at 10% reaches it
 *    long after one at 45%.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** The stopping rule refuses to call a result earlier than this, so no plan is shorter. */
export const MIN_PLAN_DAYS = Math.round(DEFAULT_MIN_RUNTIME_MS / DAY_MS);

/** Same horizon as the running projection: past a year, a day count is noise. */
export const MAX_PLAN_DAYS = 365;

/**
 * The significance detector and the attention card both ignore a result until every arm
 * has this many sessions (MIN_SESSIONS_PER_ARM in detect-significance.ts), so a plan
 * that needs fewer still waits for it. Below it the normal approximation behind the
 * sizing equation is also too rough to state a detectable lift with a straight face.
 */
export const MIN_SESSIONS_PER_ARM = 1000;

const BASE_ALPHA = 0.05;
const TARGET_POWER = 0.8;

type PlanShape = {
  controlRate: number;
  dailySessions: number;
  /** Share of traffic per arm, any scale: basis points, percentages or plain ratios. */
  weights: readonly number[];
};

export type ExperimentPlan = {
  comparisons: number;
  alpha: number;
  /** Absolute rate difference the plan is sized for. */
  absoluteEffect: number;
  requiredPerArm: number;
  /** Days until the slowest arm reaches requiredPerArm, or null when it never will. */
  days: number | null;
  /** days rounded up to whole weeks and never under MIN_PLAN_DAYS. */
  recommendedDays: number | null;
};

type PlanExperimentParams = PlanShape & {
  /** The lift worth detecting, relative to the control rate: 0.05 is +5%. */
  relativeLift: number;
};

export function planExperiment({
  controlRate,
  dailySessions,
  weights,
  relativeLift,
}: PlanExperimentParams): ExperimentPlan | null {
  const shape = readShape({ controlRate, dailySessions, weights });
  if (!shape || relativeLift <= 0) return null;

  const absoluteEffect = controlRate * relativeLift;
  // A 300% lift on a 30% rate is a 120% rate: not a plan, a typo.
  if (controlRate + absoluteEffect >= 1) return null;
  const sized = calculateRequiredSampleSize(controlRate, absoluteEffect, shape.alpha, TARGET_POWER);
  if (sized <= 0) return null;
  const requiredPerArm = Math.max(sized, MIN_SESSIONS_PER_ARM);

  const rawDays = Math.ceil(requiredPerArm / shape.slowestArmPerDay);
  const days = rawDays > MAX_PLAN_DAYS ? null : rawDays;

  return {
    comparisons: shape.comparisons,
    alpha: shape.alpha,
    absoluteEffect,
    requiredPerArm,
    days,
    // Whole weeks, so every weekday is measured the same number of times: a test that
    // runs Monday to the following Wednesday counts three Mondays' behaviour twice.
    recommendedDays: days === null ? null : Math.max(MIN_PLAN_DAYS, Math.ceil(days / 7) * 7),
  };
}

type DetectableLiftParams = PlanShape & { days: number };

/**
 * The smallest relative lift a run of `days` can reliably detect: the plan read the
 * other way round, for a stop date that has already been chosen.
 *
 * Null when the run is too short to say: under MIN_SESSIONS_PER_ARM, or when even the
 * largest possible effect still needs more traffic than the run collects.
 *
 * Solved here rather than through calculateMDE, which rounds to 0.0001 absolute. Divided
 * by a low control rate that step is large: at a 0.2% rate a true floor of 0.00034
 * rounded to 0.0003 and read as +15% where +17% is honest, and a floor small enough
 * rounded to zero. Bisection over the same sizing equation, unrounded, avoids both.
 */
export function detectableLift({ controlRate, dailySessions, weights, days }: DetectableLiftParams): number | null {
  const shape = readShape({ controlRate, dailySessions, weights });
  if (!shape || days <= 0) return null;

  const perArm = Math.floor(shape.slowestArmPerDay * days);
  if (perArm < MIN_SESSIONS_PER_ARM) return null;

  const required = (effect: number) => calculateRequiredSampleSize(controlRate, effect, shape.alpha, TARGET_POWER);
  let hi = 1 - controlRate;
  if (required(hi) > perArm) return null;

  let lo = 0;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (required(mid) > perArm) lo = mid;
    else hi = mid;
  }
  // hi is the smallest effect already within reach, so the answer never understates it.
  return hi / controlRate;
}

/** The parts of a plan that do not depend on the effect being sized for. */
function readShape({ controlRate, dailySessions, weights }: PlanShape) {
  if (!(controlRate > 0 && controlRate < 1) || !(dailySessions > 0)) return null;

  const positive = weights.filter((weight) => weight > 0);
  if (positive.length < 2) return null;

  const total = positive.reduce((sum, weight) => sum + weight, 0);
  const slowestShare = Math.min(...positive) / total;
  const comparisons = positive.length - 1;

  return {
    comparisons,
    alpha: BASE_ALPHA / comparisons,
    slowestArmPerDay: dailySessions * slowestShare,
  };
}

/** One set of assumptions, resolved against the site's figures and planned. */
export type PlanEvaluation = {
  dailySessions: number | null;
  controlRate: number | null;
  /** Where the traffic came from, so the card can say "your figure", "past tests here"... */
  trafficSource: 'yours' | 'history' | 'site' | 'none';
  rateSource: 'yours' | 'history' | 'none';
  /** The share of site traffic used, when trafficSource is 'site'. */
  siteSharePct: number | null;
  /** A saved site share could not be used: the site has no traffic measured right now. */
  shareUnavailable: boolean;
  plan: ExperimentPlan | null;
  /** Relative lift (0.05 = +5%) a run of `days` can detect under these assumptions, or null. */
  liftIn: (days: number) => number | null;
};

type PlanFigures = Pick<PlanningBaseline, 'dailySessions' | 'controlRate' | 'siteDailySessions'>;

type EvaluatePlanParams = {
  assumptions: PlanAssumptions;
  /** The website's figures, or null while they are loading or unavailable. */
  baseline: PlanFigures | null;
  /** Share of traffic per arm that receives traffic, any scale. */
  weights: readonly number[];
};

/**
 * Resolve a plan's assumptions against the website's figures, then plan it.
 *
 * One function for the Brief tab's card and the Library timeline, so a draft queued in
 * a pipeline is drawn as long as its own plan says, not a length estimated separately.
 *
 * Traffic precedence: a typed figure, then the chosen share of the site's traffic, then
 * past tests' typical traffic. With neither history nor a choice, the whole site's
 * traffic is used and labelled as such, so a site that has never run a test still gets
 * a plan, and an obviously optimistic one says so.
 */
export function evaluatePlan({ assumptions, baseline, weights }: EvaluatePlanParams): PlanEvaluation {
  const { liftPct, dailySessions: ownTraffic, siteSharePct, ratePct } = assumptions;
  const siteDaily = baseline?.siteDailySessions ?? null;
  const history = baseline?.dailySessions ?? null;

  const traffic: Pick<PlanEvaluation, 'dailySessions' | 'trafficSource' | 'siteSharePct'> =
    ownTraffic !== null
      ? { dailySessions: ownTraffic, trafficSource: 'yours', siteSharePct: null }
      : siteSharePct !== null && siteDaily !== null
        ? { dailySessions: Math.round((siteDaily * siteSharePct) / 100), trafficSource: 'site', siteSharePct }
        : history !== null
          ? { dailySessions: history, trafficSource: 'history', siteSharePct: null }
          : siteDaily !== null
            ? { dailySessions: siteDaily, trafficSource: 'site', siteSharePct: 100 }
            : { dailySessions: null, trafficSource: 'none', siteSharePct: null };

  const controlRate = ratePct !== null ? ratePct / 100 : (baseline?.controlRate ?? null);
  const { dailySessions } = traffic;
  const shape = dailySessions !== null && controlRate !== null ? { controlRate, dailySessions, weights } : null;

  return {
    ...traffic,
    controlRate,
    rateSource: ratePct !== null ? 'yours' : baseline?.controlRate ? 'history' : 'none',
    shareUnavailable: siteSharePct !== null && siteDaily === null,
    plan: shape ? planExperiment({ ...shape, relativeLift: liftPct / 100 }) : null,
    liftIn: (days) => (shape ? detectableLift({ ...shape, days }) : null),
  };
}
